import React from 'react';
import Loadable from 'react-loadable';
import renderHtml from 'htmr';
import { getPrismicSizedImageUrl, getPrismicStructuredTextPlain } from '../../utils/functions';
import './ReadAboutAuthor.scss';

const LoadableCarousel = Loadable({
  loader: () => import('../Carousel/CarouselSlider'),
  loading: () => null,
});

const AUTHOR_GALLERY_CAROUSEL = {
  responsiveClass: true,
  margin: 20,
  responsive: {
    0: {
      items: 1,
      nav: false,
      stagePadding: 40,
    },
    576: {
      items: 2,
      nav: false,
      stagePadding: 40,
    },
    768: {
      items: 3,
      nav: false,
      loop: false,
    },
    1200: {
      items: 3,
      nav: true,
      loop: false,
    },
  },
};

const SUKI_TEAM_MEMBER_UID = 'suki-is-passionate-about-winning-people-for-jesus-and';

const SUKI_ABOUT_BIO =
  'Sukeshinie (Suki) Goonatilleke is an author, speaker, freelance writer and trainer. Her work combines her love of history with her love of storytelling. She lives with her husband and two kids in Melbourne, Australia.';

const isSukiTeamMember = (teamMember, displayName, data) => {
  if (teamMember?.uid === SUKI_TEAM_MEMBER_UID) {
    return true;
  }
  const name = (displayName || getPrismicStructuredTextPlain(data?.name) || '').toLowerCase();
  return name.includes('sukeshinie') || name.includes('goonatilleke');
};

const getMemberName = (data) => getPrismicStructuredTextPlain(data?.name);

const getMemberRole = (data) => getPrismicStructuredTextPlain(data?.ministryRole);

const formatAboutAuthorRole = (roleText) =>
  typeof roleText === 'string' ? roleText.replace(/\s*\/Manager\b/gi, '').trim() : '';

const getMemberBioHtml = (data) => {
  if (typeof data?.bio?.text === 'string' && data.bio.text.trim()) {
    return `<p>${data.bio.text.trim()}</p>`;
  }
  if (Array.isArray(data?.bio)) {
    const joined = data.bio.map((block) => block?.text).filter(Boolean).join(' ');
    return joined ? `<p>${joined}</p>` : '';
  }
  return '';
};

const collectGalleryUrls = (data, articleGalleryUrls = []) => {
  const fromGroup =
    data?.aboutAuthorGallery
      ?.map((item) => item?.image?.url || item?.image?.fluid?.src)
      .filter(Boolean) || [];

  if (fromGroup.length) {
    return fromGroup;
  }

  if (Array.isArray(articleGalleryUrls) && articleGalleryUrls.length) {
    return articleGalleryUrls;
  }

  return [];
};

const renderGalleryImage = (url, index) => (
  <div className="r_s_carousel-item" key={`${url}-${index}`}>
    <div className="media ratio_9-16">
      <div
        className="m_overlay-image bgImage"
        style={{ backgroundImage: `url(${getPrismicSizedImageUrl(url, 900)})` }}
        role="img"
        aria-label=""
      />
    </div>
  </div>
);

export default function ReadAboutAuthor({ teamMember, displayName, articleGalleryUrls }) {
  const data = teamMember?.data;
  if (!data) return null;

  const name = displayName || getMemberName(data);
  const role = formatAboutAuthorRole(getMemberRole(data));
  const bioHtml = isSukiTeamMember(teamMember, displayName, data)
    ? `<p>${SUKI_ABOUT_BIO}</p>`
    : getMemberBioHtml(data);
  const portraitUrl = data?.portraitPhoto?.url || data?.portraitPhoto?.fluid?.src;
  const galleryUrls = collectGalleryUrls(data, articleGalleryUrls);

  if (!name && !bioHtml && !portraitUrl) {
    return null;
  }

  const galleryBlock =
    galleryUrls.length > 0 ? (
      <div className="read-about-author__gallery-wrap">
        <div className="read-about-author__carousel r_s_carousel">
          {galleryUrls.length === 1 ? (
            <div className="read-about-author__carousel-single">{renderGalleryImage(galleryUrls[0], 0)}</div>
          ) : (
            <LoadableCarousel className="r_s_carousel-list owl-carousel" {...AUTHOR_GALLERY_CAROUSEL}>
              {galleryUrls.map(renderGalleryImage)}
            </LoadableCarousel>
          )}
        </div>
      </div>
    ) : null;

  return (
    <section id="about-author" className="read-about-author section">
      <div className="wrapper wrapper-xl pd-x-md">
        <div className="read-about-author__panel bg-gray">
          <div className="wrapper wrapper-md pd-x-sm">
            <div className="wrapper-sm read-about-author__inner read-single-content-align">
              <h2 className="read-about-author__title">About the Author</h2>
              {galleryBlock}
              <div className="read-about-author__head">
                {portraitUrl ? (
                  <div
                    className="read-about-author__portrait bgImage"
                    style={{ backgroundImage: `url(${getPrismicSizedImageUrl(portraitUrl, 320)})` }}
                    role="img"
                    aria-label={name || 'Author portrait'}
                  />
                ) : null}
                <div className="read-about-author__intro">
                  {name ? <h3 className="read-about-author__name">{name}</h3> : null}
                  {role ? <p className="read-about-author__role">{role}</p> : null}
                </div>
              </div>

              {bioHtml ? <div className="read-about-author__bio">{renderHtml(bioHtml)}</div> : null}

              <div className="read-about-author__rule" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
